import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Props partagees par les deux rendus possibles de NavItem (`<a>` ou `<button>`).
 *
 * Le brand book Taki fixe les dimensions exactes : hauteur 52, rayon 15,
 * padding horizontal 18, libelle Inter 500/15. L'etat actif passe en tint lime
 * (`bg-accent-tint` + bordure `accent-tint-border`), texte fonce semibold, et
 * affiche un petit point `accent-dot` de 7px a droite.
 */
interface NavItemBaseProps {
  /** Icone de gauche (20px conseille, theme via `currentColor`). */
  icon?: ReactNode;
  /** Libelle de l'item de navigation. */
  label: ReactNode;
  /**
   * Item actif : fond tint lime, bordure, texte fonce semibold et point a
   * droite. Active aussi le point sauf si `showDot` est explicitement `false`.
   */
  active?: boolean;
  /**
   * Affiche (ou masque) le point `accent-dot` a droite. Par defaut, le point
   * suit l'etat `active`. Mettre `false` pour un item actif sans point.
   */
  showDot?: boolean;
}

/** NavItem rendu comme lien (`<a>`) : fourni un `href`. */
export type NavItemAnchorProps = NavItemBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof NavItemBaseProps> & {
    href: string;
  };

/** NavItem rendu comme bouton (`<button>`) : pas de `href`. */
export type NavItemButtonProps = NavItemBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof NavItemBaseProps> & {
    href?: undefined;
  };

/**
 * Item de navigation laterale Taki. Rendu polymorphe : un `<a>` si `href` est
 * fourni, sinon un `<button>`. Composant de presentation pur (l'etat actif est
 * pilote par le parent via `active`).
 */
export type NavItemProps = NavItemAnchorProps | NavItemButtonProps;

const baseClasses =
  'group flex h-[3.25rem] w-full items-center gap-3 rounded-[0.9375rem] px-[1.125rem] text-left font-sans text-[0.9375rem] font-medium transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-accent-border';

const stateClasses = {
  default: 'border border-transparent bg-transparent text-ink-500 hover:bg-surface-3',
  active: 'border border-accent-tint-border bg-accent-tint font-semibold text-ink-900',
} as const;

const dotClasses = 'ml-auto h-[0.4375rem] w-[0.4375rem] shrink-0 rounded-pill bg-accent-dot';

function isAnchor(props: NavItemProps): props is NavItemAnchorProps {
  return typeof props.href === 'string';
}

/**
 * Item de nav lateral (h52 / r15). Default transparent, hover `surface-3`,
 * actif tint lime + point. Rendu `<a>` avec `href`, sinon `<button>`.
 */
export const NavItem = forwardRef<HTMLAnchorElement | HTMLButtonElement, NavItemProps>(
  function NavItem(props, ref) {
    const { icon, label, active = false, showDot, className } = props;
    const withDot = showDot ?? active;
    const content = (
      <>
        {icon != null && <span className="flex shrink-0 items-center justify-center">{icon}</span>}
        <span className="flex-1 truncate">{label}</span>
        {withDot && <span aria-hidden className={dotClasses} />}
      </>
    );
    const classes = cn(baseClasses, active ? stateClasses.active : stateClasses.default, className);

    if (isAnchor(props)) {
      const { icon: _i, label: _l, active: _a, showDot: _s, className: _c, ...rest } = props;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes}
          aria-current={active ? 'page' : undefined}
          {...rest}
        >
          {content}
        </a>
      );
    }

    const { icon: _i, label: _l, active: _a, showDot: _s, className: _c, href: _h, type, ...rest } =
      props;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type ?? 'button'}
        className={classes}
        aria-current={active ? 'page' : undefined}
        {...rest}
      >
        {content}
      </button>
    );
  },
);
